import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {StorageMap} from '@ngx-pwa/local-storage';
import {ToastController, LoadingController, Platform, NavController} from '@ionic/angular';
import {DateTime, Interval} from 'luxon';
import {Alumno, Aula, Aviso, SemanasProyecto, Token, Trabajador, Vacaciones} from '../interfaces/interfaces';
import packageJSON from '../../../package.json';

@Injectable({
  providedIn: 'root'
})
export class SharedService {

  APP = 'AGENDA';
  HEADER = 'babycloud-token';
  VERSION = packageJSON.version;
  SITEKEYV3 = environment.tokenRecaptcha;
  SINIMAGEN = 'assets/img/noimage.png';
  SINFOTOALUMNO = 'assets/img/sinfotoalumno.jpg';
  SINFOTOEDUCADORA = 'assets/img/educadora.png';
  LOADER = 'assets/img/preloader.gif';
  ZERODATE = '0000-00-00';
  TIME_CIRCULARES = 20;
  MESES = ['ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO', 'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'];
  changelog = '<strong class="primary-sec">' + this.VERSION + '</strong><br>' +
    '- Vacaciones actualizadas<br>' +
    '- Actualización del Framework<br>' +
    '- Fotos en los informes<br>';

  constructor(
    private storage: StorageMap,
    private toaster: ToastController,
    private loadingCtl: LoadingController,
    private platform: Platform,
    private navCtrl: NavController
  ) {}

  START = (token: string, loading?: HTMLIonLoadingElement) => {
    this.setToken(token).subscribe({
      next: async () => {
        if (loading) await loading.dismiss();
        await this.navCtrl.navigateRoot('/home');
      }, error: async () => {
        if (loading) await loading.dismiss();
        await this.mostrarToast('(E02) Algo no ha funcionado bien...', true);
      }
    })
  }

  LOGOUT = () => {
    this.clearItems().subscribe(async () => {
      await this.navCtrl.navigateRoot('/login');
    })
  }

  ERROR = async (err: any, numero?: string) => {
    if (err && (err?.status === 401 || err?.status === 403 || err?.status === 402 || err?.status === 405)) {
      await this.mostrarToast('E050 ERROR DE SEGURIDAD', true);
      this.LOGOUT();
      return
    }
    if (err?.error && err?.status) {
      const txt = numero ? 'E' + numero + ' ' + err.error.message : err.error.message;
      await this.mostrarToast(txt, true);
      return
    }
    if (!err?.status) {
      await this.mostrarToast('E010 No hay conexión', true);
      return
    }
    await this.mostrarToast('E0000 Error desconocido', true)
  }

  /** SETTERS **/
  setItem = (key: string, value: any): Observable<any> => this.storage.set(key, value);

  getItem = (key: string): Observable<any> => this.storage.get(key);

  clearItems = (): Observable<any> => this.storage.clear();

  /*** TOKEN ***/
  getToken = (): Observable<string> => this.getItem('TOKEN');

  setToken = (value: string): Observable<any> => this.storage.set('TOKEN', value);

  validarToken = (): Observable<boolean> => {
    return this.parseToken().pipe(
      map(t => t && t.exp > DateTime.local().toSeconds() && t.role > -1)
    )
  }

  parseToken = (): Observable<Token> => {
    return this.getToken().pipe(
      map(t => {
        try {
          const baseUrl: string = t.split('.')[1];
          const base64: string = baseUrl.replace('-', '+').replace('_', '/');
          return JSON.parse(window.atob(base64))
        } catch (err) {
          return null
        }
      })
    )
  }

  /*** HELPERS ***/

  /*** DEVUELVE false si son distintos ***/
  checkVersion = (version: string): boolean => {
    const matrix: string[] = this.VERSION.split('.'),
      versionActual: string = `${matrix[0]}.${matrix[1]}.${matrix[2]}`;
    return versionActual !== version
  }

  showVacaciones = (fecha: string): boolean => {
    if (fecha === '') return false;
    const day: number = +fecha.split('/')[0],
      month: number = +fecha.split('/')[1],
      date: DateTime = DateTime.now().set({day: day, month: month});
    return DateTime.now() > date && DateTime.now().month < 9;
  }

  mostrarToast = async (msg?: string, error: boolean = false, position: 'bottom' | 'top' | 'middle' = 'bottom') => {
    const toast = await this.toaster.create({
      message: msg || 'Mensaje de ERROR de la aplicación',
      duration: 3000,
      position: position,
      color: error ? 'danger' : 'dark'
    })
    await toast.present()
  }

  load = (text?: string) => this.loadingCtl.create({
      message: text ? text : '',
      animated: !text,
      spinner: text ? 'bubbles' : 'dots'
    })

  handleFileUrl = (url: string): string => {
    const uri = new URL(url);
    return uri.pathname.slice(1)
  }

  apellidosHelper = (alumno: Alumno): Alumno => {
    if (alumno) {
      if (alumno.ap1 === '') {
        alumno.ap1 = alumno.apellidos.split(' ')[0];
        alumno.ap2 = alumno.apellidos.split(' ')[1] || ''
      }
      alumno.nombreCompleto = alumno.nombre + ' ' + alumno.ap1 + ' ' + alumno.ap2;
    }
    return alumno
  }

  go = (direccion: string) => this.navCtrl.navigateForward('/' + direccion);

  ancho = () => this.platform.width();

  getCursoActual = (): string => {
    const hoy = new Date(),
      actual: number = (hoy.getMonth() < 8) ? hoy.getFullYear() - 1 : hoy.getFullYear();
    return actual + '-' + (actual + 1)
  }

  quitarAcentos = (value: string): string => value
    .replace(/á/g, 'a')
    .replace(/é/g, 'e')
    .replace(/í/g, 'i')
    .replace(/ó/g, 'o')
    .replace(/ú/g, 'u')

  getBack = () => this.navCtrl.back();

  verDoc = (url: string) => window.open(url, '_blank');

  mask = (tel: string): string => {
    if (tel) {
      tel = String(tel);
      if (tel.length === 9) {
        return tel.slice(-9, -6) + ' ' + tel.slice(-6, -3) + ' ' + tel.slice(-3);
      }
    }
    return tel
  }

  buildSemanasVerano = (): string[] => {
    const firstjulio: Date = new Date((new Date()).getFullYear(), 6, 1),
      textosemanas: string[] = [];
    let fj: number, fa: number;
    switch (firstjulio.getDay()) {
      case 1:
        textosemanas[0] = '1-7 DE JULIO';
        textosemanas[4] = '29 JULIO-4 AGOSTO';
        textosemanas[8] = '26-31 DE AGOSTO';
        textosemanas[9] = '';
        fj = 8;
        fa = 5;
        break;
      case 2:
        textosemanas[0] = '1-6 DE JULIO';
        textosemanas[4] = '28 JULIO-3 AGOSTO';
        textosemanas[8] = '25-31 DE AGOSTO';
        textosemanas[9] = '';
        fj = 7;
        fa = 4;
        break;
      case 3:
        textosemanas[0] = '1-5 DE JULIO';
        textosemanas[4] = '27 JULIO-2 AGOSTO';
        textosemanas[8] = '24-30 DE AGOSTO';
        textosemanas[9] = '31 DE AGOSTO';
        fj = 6;
        fa = 3;
        break;
      case 4:
        textosemanas[0] = '1-4 DE JULIO';
        textosemanas[4] = '26 JULIO-1 AGOSTO';
        textosemanas[8] = '23-29 DE AGOSTO';
        textosemanas[9] = '30-31 DE AGOSTO';
        fj = 5;
        fa = 2;
        break;
      case 5:
        textosemanas[0] = '1-3 DE JULIO';
        textosemanas[4] = '25-31 DE JULIO';
        textosemanas[8] = '22-28 DE AGOSTO';
        textosemanas[9] = '29-31 DE AGOSTO';
        fj = 4;
        fa = 1;
        break;
      case 6:
        textosemanas[0] = '3-9 DE JULIO';
        textosemanas[4] = '31 JULIO-6 AGOSTO';
        textosemanas[8] = '28-31 DE AGOSTO';
        textosemanas[9] = '';
        fj = 10;
        fa = 7;
        break;
      case 0:
        textosemanas[0] = '2-8 DE JULIO';
        textosemanas[4] = '30 JULIO-5 AGOSTO';
        textosemanas[8] = '27-31 DE AGOSTO';
        textosemanas[9] = '';
        fj = 9;
        fa = 6;
        break;
    }
    for (let i = 0; i < 3; i++) textosemanas[i + 1] = +(fj + 7 * i) + '-' + +(fj + 7 * i + 6) + ' DE JULIO';
    for (let i = 0; i < 3; i++) textosemanas[i + 5] = +(fa + 7 * i) + '-' + +(fa + 7 * i + 6) + ' DE AGOSTO';
    return textosemanas
  }

  buildSemanas = (): SemanasProyecto[] => {
    const year: number = +this.getCursoActual().split('-')[0],
      primerosept: number = new Date(year, 8, 1).getDay(),
      semanas: Array<{semana: number, limites: string}> = [],
      limit: DateTime = DateTime.local(year + 1, 8, 31);
    let siguiente: DateTime, i = 2;
    if (primerosept > 0 && primerosept < 6) {
      const final = (8 - primerosept) + '/09/' + year,
        inicio: DateTime = DateTime.local(year, 9, 1).minus({days: primerosept - 1});
      semanas.push({
        semana: 1,
        limites: inicio.toLocaleString(DateTime.DATE_SHORT) + '-' + final
      });
      siguiente = DateTime.local(year, 9, 9 - primerosept)
    } else {
      if (primerosept === 0) {
        semanas.push({
          semana: 1,
          limites: '2/09/' + year + '-8/09/' + year
        });
        siguiente = DateTime.local(year, 9, 9)
      } else {
        semanas.push({
          semana: 1,
          limites: '3/09/' + year + '-9/09/' + year
        });
        siguiente = DateTime.local(year, 9, 10)
      }
    }
    while (siguiente < limit) {
      semanas.push({
        semana: i,
        limites: siguiente.toLocaleString(DateTime.DATE_SHORT) + '-' +
          siguiente.plus({days: 6}).toLocaleString(DateTime.DATE_SHORT)
      });
      siguiente = siguiente.plus({days: 7});
      i++
    }
    return semanas
  }

  getSemana = (): number => {
    let valor = 0;
    this.buildSemanas().forEach(s => {
      if (Interval.fromDateTimes(DateTime.fromFormat(s.limites.split('-')[0], 'd/L/yyyy'),
        DateTime.fromFormat(s.limites.split('-')[1], 'd/L/yyyy'))
        .contains(DateTime.local())) {
        valor = s.semana
      }
    });
    return valor
  }

  getHoraCompleta = (time: number | string): string => {
    let valor = '';
    if (time && +time > 0) {
      let horas: string | number = Math.floor(+time / 3600),
        minutos: string | number = (+time - horas * 3600) / 60;
      horas = horas < 10 ? '0' + horas : '' + horas;
      minutos = minutos < 10 ? '0' + minutos : '' + minutos;
      valor = horas + ':' + minutos
    }
    return valor
  }

  setHora = (hora: string): number => {
    const horas: number = hora ? +hora.split(':')[0] : 0,
      minutos: number = hora ? +hora.split(':')[1] : 0;
    return this.isNatural(horas) && this.isNatural(minutos) && (horas < 24 || minutos < 60) ? horas * 3600 + minutos * 60 : 0
  }

  private isNumber = (n: any): boolean => !isNaN(parseFloat(n)) && isFinite(n);

  private isInt = (n: any): boolean => this.isNumber(n) && n % 1 === 0;

  private isNatural = (n: any): boolean => this.isInt(n) && n >= 0;

  sortChildByEntryTime = (a: Vacaciones, b: Vacaciones): number => this.setHora(a.hentrada) - this.setHora(b.hentrada);

  sortAulaByNumber = (a: Aula, b: Aula): number => a.numeroAula - b.numeroAula;

  sortByMomento = (a: Aviso, b: Aviso): number => DateTime.fromSQL(b.momento).toSeconds() - DateTime.fromSQL(a.momento).toSeconds();

  sortByName = (a: Trabajador, b: Trabajador) => a.nombre.localeCompare(b.nombre);

  sortByCompletName = (a: Alumno, b: Alumno) => a.nombreCompleto.localeCompare(b.nombreCompleto);

  resize = (srcBase64: string, maxWidth: number, maxHeight: number): Promise<string> => new Promise((resolve, reject) => {
    const img: HTMLImageElement = new Image();
    img.src = srcBase64;
    img.onerror = error => reject(error);
    img.onload = () => {
      const width: number = img.width,
        height: number = img.height,
        canvas: HTMLCanvasElement = document.createElement('canvas'),
        ctx: CanvasRenderingContext2D = canvas.getContext('2d');
      let newWidth: number, newHeight: number;
      if (width <= maxWidth && height <= maxHeight) {
        newWidth = width;
        newHeight = height;
      } else {
        if (width > height) {
          newHeight = height * (maxWidth / width);
          newWidth = maxWidth;
        } else {
          newWidth = width * (maxHeight / height);
          newHeight = maxHeight;
        }
      }
      canvas.width = newWidth;
      canvas.height = newHeight;
      ctx.drawImage(img, 0, 0, newWidth, newHeight);
      resolve(canvas.toDataURL())
    }
  })

  base64ToFile = async (data64: string, filename: string): Promise<File> => {
    try {
      const array: string[] = data64.split(','),
        mime: string = array[0].match(/:(.*?);/)[1],
        bstr: string = atob(array[1]);
      let n: number = bstr.length;
      const u8arr: Uint8Array = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
      }
      return new File([u8arr], filename, {type: mime})
    } catch {
      throw 'ERROR EN EL PROCESO'
    }
  }

}
